import { useState, useEffect, useMemo } from 'react';
import {
  GitBranch, Plus, Trash2, Play, Pause, RefreshCw, AlertTriangle, CheckCircle,
  Loader2, ChevronRight, Activity, TrendingUp, TrendingDown, Wind, Mountain,
  Eye, X,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';
import {
  listConditionalStrategies, getConditionalStrategy, createConditionalStrategy,
  activateConditionalStrategy, pauseConditionalStrategy, deleteConditionalStrategy,
  evaluateConditionalStrategy, simulateConditionalStrategy,
  getExchangeMarkets, getExchangeConnections, getExchangeTicker,
  listGridBots, listSmartTrades, listDcaSafetyBots, getStrategyTemplates,
  listStrategies,
  setConditionalAutoExecute,
} from '../services/api';
import { Zap, Sparkles } from 'lucide-react';

// ─── useLivePrice ────────────────────────────────────────────────────────────
//
// Shared hook for the create form + sub-forms so users see the current market
// price next to every range/TP/SO input without opening a separate browser tab.
// Refreshes every 15s while the page is open. Returns null while loading or on
// error so callers can render gracefully ("Price unavailable" vs hiding the
// quick-fill buttons).
function useLivePrice(exchangeId, symbol) {
  const [price, setPrice] = useState(null);
  const [stale, setStale] = useState(false);
  useEffect(() => {
    if (!exchangeId || !symbol || !symbol.includes('/')) {
      setPrice(null);
      return;
    }
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const res = await getExchangeTicker(exchangeId, symbol);
        const p = res?.ticker?.last ?? res?.last ?? res?.data?.last;
        if (!cancelled && typeof p === 'number' && p > 0) {
          setPrice(p);
          setStale(false);
        }
      } catch {
        if (!cancelled) setStale(true);
      }
    };
    tick();
    timer = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [exchangeId, symbol]);
  return { price, stale };
}

// Tiny placeholder shown for ~50ms before the per-exchange CCXT markets load.
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Same sort as Algo Orders + Grid Bot + DCA Safety so popular pairs surface
// at the top of the per-exchange list.
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

const REGIMES = [
  { id: 'strong_uptrend',   label: 'Strong Uptrend',   icon: TrendingUp,   color: 'emerald' },
  { id: 'uptrend',          label: 'Uptrend',          icon: TrendingUp,   color: 'emerald' },
  { id: 'range',            label: 'Range / Sideways', icon: Activity,     color: 'amber' },
  { id: 'downtrend',        label: 'Downtrend',        icon: TrendingDown, color: 'red' },
  { id: 'strong_downtrend', label: 'Strong Downtrend', icon: TrendingDown, color: 'red' },
  { id: 'volatile',         label: 'High Volatility',  icon: Wind,         color: 'violet' },
];

const STRATEGY_OPTIONS = [
  { id: 'mkt-001', label: 'Alpha Momentum Pro (trend)' },
  { id: 'mkt-002', label: 'DCA Guardian (mean reversion)' },
  { id: 'mkt-003', label: 'Breakout Hunter (high vol)' },
  { id: 'mkt-004', label: 'Bollinger Squeeze Pro (range)' },
  { id: 'grid-tight',  label: 'Grid Bot (tight range)' },
  { id: 'grid-medium', label: 'Grid Bot (medium range)' },
  { id: 'grid-wide',   label: 'Grid Bot (wide range)' },
];

const REFERENCE_SYMBOLS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];
const TIMEFRAMES = ['15m', '30m', '1h', '4h', '1d'];

const COLOR_CLASSES = {
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  amber:   'text-amber-400 bg-amber-500/10 border-amber-500/30',
  red:     'text-red-400 bg-red-500/10 border-red-500/30',
  violet:  'text-violet-400 bg-violet-500/10 border-violet-500/30',
};

function regimeMeta(id) {
  return REGIMES.find(r => r.id === id) || { id, label: id, color: 'amber', icon: Activity };
}

export default function ConditionalStrategyPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create'
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);

  async function loadList() {
    try {
      const res = await listConditionalStrategies();
      setStrategies(res?.data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadList(); }, []);

  // Polling: refresh list + selected strategy details every 15s so regime
  // switches and active-rule changes surface without a hard refresh.
  const interval = useActivePolling(15000, activeTab === 'list');
  useEffect(() => {
    if (!interval) return;
    const tick = async () => {
      try {
        const res = await listConditionalStrategies();
        const list = res?.data || [];
        setStrategies(list);
        if (selected) {
          const updated = list.find(s => s.id === selected.id);
          if (updated) setSelected(updated);
          try {
            const detail = await getConditionalStrategy(selected.id);
            if (detail) setDetails(detail?.data);
          } catch {}
        }
      } catch (err) { console.error('Conditional list tick failed', err); }
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, selected?.id]);

  useEffect(() => {
    if (!selected) { setDetails(null); return; }
    (async () => {
      try {
        const res = await getConditionalStrategy(selected.id);
        setDetails(res?.data);
      } catch (err) { console.error(err); }
    })();
  }, [selected]);

  async function refreshAfterAction(id) {
    const res = await listConditionalStrategies();
    const list = res?.data || [];
    setStrategies(list);
    if (selected?.id === id) {
      const updated = list.find(s => s.id === id);
      if (updated) setSelected(updated);
      const detail = await getConditionalStrategy(id).catch(() => null);
      if (detail) setDetails(detail?.data);
    }
  }

  async function handleActivate(id) {
    try { await activateConditionalStrategy(id); await refreshAfterAction(id); }
    catch (err) { alert(err.message); }
  }
  async function handlePause(id) {
    try { await pauseConditionalStrategy(id); await refreshAfterAction(id); }
    catch (err) { alert(err.message); }
  }
  // Phase 1 (2026-06-08): toggle auto-execute on a conditional strategy.
  // Backend persists the flag; the engine reads it on the next regime
  // evaluation to decide whether to deploy / stop the inner bot.
  async function handleToggleAutoExecute(id, enabled) {
    try {
      await setConditionalAutoExecute(id, enabled);
      await refreshAfterAction(id);
    } catch (err) {
      alert('Could not change auto-execute setting: ' + err.message);
    }
  }
  async function handleDelete(id) {
    if (!confirm('Delete this conditional strategy? Cannot be undone.')) return;
    try {
      await deleteConditionalStrategy(id);
      if (selected?.id === id) setSelected(null);
      await loadList();
    } catch (err) { alert(err.message); }
  }
  async function handleEvaluate(id) {
    try {
      const res = await evaluateConditionalStrategy(id);
      const evalData = res?.data;
      // Surface the result so users see something happened — silent
      // success when state already populated was misread as "broken".
      if (evalData?.error) {
        alert(`Evaluation error: ${evalData.error}`);
      } else if (evalData?.skipped) {
        alert(`Evaluation skipped: ${evalData.reason}. Tip: click Activate first, or this is a server-side issue.`);
      } else if (evalData?.regime) {
        const regimeLabel = evalData.regime?.regime || 'unknown';
        const ruleName = evalData.activeRuleName || 'fallback';
        const volPct = evalData.regime?.volatilityPct?.toFixed(2);
        alert(`✓ Evaluated successfully\n\nRegime: ${regimeLabel}\nActive rule: ${ruleName}\nVolatility: ${volPct}%\nRSI: ${evalData.regime?.rsi?.toFixed(1)}\n\nDetails panel will refresh.`);
      }
      const detail = await getConditionalStrategy(id);
      setDetails(detail?.data);
      return evalData;
    } catch (err) {
      alert(`Evaluate failed: ${err.message}`);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <GitBranch className="w-7 h-7 text-violet-400" />
          <h1 className="text-2xl font-bold text-white">Conditional / Composite Bots</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">Trader+</span>
        </div>
        <p className="text-sm text-gray-400">
          Chain market regimes to strategies — "if BTC is in a strong uptrend run momentum, else if ranging run grid, else pause." <span className="text-emerald-400">Regimes derived from real OHLCV — no simulation.</span>
        </p>
      </header>

      {/* Phase 2 banner (2026-06-09) — Auto-execute supports Grid Bot AND
          DCA Safety as inner bot types. Each rule chooses which one to deploy,
          and the master autoExecute toggle gates whether the engine actually
          spins them up on regime entry / tears them down on regime exit. */}
      <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-2xl p-4 flex items-start gap-3">
        <Zap className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs">
          <p className="text-emerald-300 font-semibold mb-1">Auto-execute now covers Grid AND DCA Safety</p>
          <p className="text-emerald-200/80 leading-relaxed">
            Each rule can <strong>deploy a Grid Bot</strong> for range regimes or a <strong>DCA Safety bot</strong> for downtrend/dip regimes, and stop them automatically on regime exit. Toggle <em>Auto-execute on regime switch</em> on each strategy to turn it from signal-only into a set-and-forget orchestrator. Rate-limited to one switch per 30 min.
          </p>
        </div>
      </div>

      <div className="border-b border-dark-700/50 flex gap-1">
        <button onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'list' ? 'border-violet-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          My Strategies {strategies.length > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-dark-700 text-gray-400">{strategies.length}</span>}
        </button>
        <button onClick={() => setActiveTab('create')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'create' ? 'border-violet-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Create New
        </button>
      </div>

      {activeTab === 'list' && (
        <ListPanel
          strategies={strategies} loading={loading}
          selected={selected} setSelected={setSelected} details={details}
          onActivate={handleActivate} onPause={handlePause} onDelete={handleDelete} onEvaluate={handleEvaluate}
          onToggleAutoExecute={handleToggleAutoExecute}
        />
      )}
      {activeTab === 'create' && (
        <CreatePanel onCreated={() => { loadList(); setActiveTab('list'); }} />
      )}
    </div>
  );
}

// ─── List Panel ──────────────────────────────────────────────────────────────

function ListPanel({ strategies, loading, selected, setSelected, details, onActivate, onPause, onDelete, onEvaluate, onToggleAutoExecute }) {
  // Same Active / Paused / All filter pattern as Algo Orders, Grid, DCA.
  const [statusFilter, setStatusFilter] = useState('active');

  const counts = useMemo(() => ({
    active: strategies.filter(s => s.status === 'active').length,
    paused: strategies.filter(s => s.status !== 'active').length,
    all: strategies.length,
  }), [strategies]);

  const filteredStrategies = useMemo(() => {
    if (statusFilter === 'active') return strategies.filter(s => s.status === 'active');
    if (statusFilter === 'paused') return strategies.filter(s => s.status !== 'active');
    return strategies;
  }, [strategies, statusFilter]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>;
  }
  if (strategies.length === 0) {
    return (
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
        <GitBranch className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No conditional strategies yet</p>
        <p className="text-sm text-gray-500">Switch to <span className="text-violet-400">Create New</span> to build your first regime-aware strategy.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 space-y-2">
        {/* Filter tabs */}
        <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700/50 mb-2 text-xs">
          {[
            { id: 'active', label: 'Active', count: counts.active },
            { id: 'paused', label: 'Paused', count: counts.paused },
            { id: 'all',    label: 'All',    count: counts.all    },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatusFilter(t.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                statusFilter === t.id
                  ? 'bg-violet-500/15 text-violet-300'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${statusFilter === t.id ? 'bg-violet-500/20' : 'bg-dark-700'}`}>{t.count}</span>
            </button>
          ))}
        </div>
        {filteredStrategies.length === 0 ? (
          <div className="bg-dark-800/50 rounded-xl border border-dark-700/50 p-6 text-center">
            <p className="text-sm text-gray-500">No {statusFilter === 'active' ? 'active' : statusFilter === 'paused' ? 'paused' : ''} strategies.</p>
          </div>
        ) : filteredStrategies.map(s => {
          const active = selected?.id === s.id;
          return (
            <button key={s.id} onClick={() => setSelected(s)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${active ? 'bg-violet-500/10 border-violet-400/40' : 'bg-dark-800/50 border-dark-700/50 hover:border-dark-600'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-white">{s.name}</span>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
                  {s.status}
                </span>
              </div>
              <p className="text-[10px] text-gray-600 mt-0.5">Created {new Date(s.createdAt).toLocaleDateString()}</p>
            </button>
          );
        })}
      </div>

      <div className="lg:col-span-2">
        {selected && details ? (
          <DetailsCard config={details.config} state={details.state} onActivate={onActivate} onPause={onPause} onDelete={onDelete} onEvaluate={onEvaluate} onToggleAutoExecute={onToggleAutoExecute} />
        ) : (
          <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
            <p className="text-sm text-gray-500">Select a strategy to see its rules, active regime, and switch history.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailsCard({ config, state, onActivate, onPause, onDelete, onEvaluate, onToggleAutoExecute }) {
  const [evaluating, setEvaluating] = useState(false);
  const [togglingAutoExecute, setTogglingAutoExecute] = useState(false);
  const autoExecute = !!config.autoExecute;
  const controlledBots = state?.controlledBotIds || [];
  // Phase 3 risk surface (2026-06-09).
  const capitalBudget = Number(config.capitalBudget) || 0;
  const maxDD = config.maxDrawdownPct;
  const lastEquity = Number(state?.lastEquity) || capitalBudget;
  const peakEquity = Number(state?.peakEquity) || capitalBudget;
  const drawdownPct = Number(state?.currentDrawdownPct) || 0;
  const killSwitch = state?.killSwitchFired || null;

  async function toggleAutoExecuteHandler(e) {
    if (!onToggleAutoExecute) return;
    setTogglingAutoExecute(true);
    try { await onToggleAutoExecute(config.id, e.target.checked); }
    finally { setTogglingAutoExecute(false); }
  }
  async function evalNow() {
    setEvaluating(true);
    await onEvaluate(config.id);
    setEvaluating(false);
  }

  const regime = state?.regime;
  const regimeM = regime ? regimeMeta(regime.regime) : null;
  const RegimeIcon = regimeM?.icon || Activity;

  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-white">{config.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{config.description || 'No description'}</p>
          <p className="text-[10px] text-gray-600 mt-1 font-mono">
            Reference: {config.referenceExchange} {config.referenceSymbol} · {config.timeframe}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={evalNow} disabled={evaluating} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${evaluating ? 'animate-spin' : ''}`} /> Evaluate
          </button>
          {config.status === 'active' ? (
            <button onClick={() => onPause(config.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20">
              <Pause className="w-3 h-3" /> Pause
            </button>
          ) : (
            <button onClick={() => onActivate(config.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20">
              <Play className="w-3 h-3" /> Activate
            </button>
          )}
          <button onClick={() => onDelete(config.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Auto-execute toggle — Phase 1 (2026-06-08). When ON the engine
          actually deploys / stops the inner bot listed in each rule's
          thenBotConfig on regime switch. Hidden if onToggleAutoExecute isn't
          wired so we degrade gracefully when used from old call sites. */}
      {onToggleAutoExecute && (
        <div className={`rounded-xl border p-3 ${autoExecute ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-dark-700/40 border-dark-600/40'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoExecute}
              disabled={togglingAutoExecute}
              onChange={toggleAutoExecuteHandler}
              className="mt-0.5 accent-emerald-500"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                Auto-execute on regime switch
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${autoExecute ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-gray-500/15 text-gray-400 border border-gray-500/30'}`}>
                  {autoExecute ? 'ON' : 'OFF'}
                </span>
              </p>
              <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                {autoExecute
                  ? 'When the regime changes, this strategy will automatically stop the previously-active inner bot and deploy the new rule\'s bot. Rate-limited to one switch per 30 min.'
                  : 'When the regime changes, this strategy will only RECORD which rule is active. It does not start or stop any bots. Toggle on to make it act autonomously.'}
              </p>
              {state?.lastActionError && (
                <p className="text-red-300 text-[11px] mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Last auto-deploy failed: {state.lastActionError}
                </p>
              )}
            </div>
          </label>
        </div>
      )}

      {/* Phase 3 kill-switch fired banner (2026-06-09). Loudest possible UI —
          red border, explanatory text, and a hint that re-activating resets
          the high-water mark. Drawn before everything else so it can't be
          missed. */}
      {killSwitch && (
        <div className="rounded-xl border-2 border-red-500/60 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-300 text-sm font-bold mb-1">
                Drawdown kill-switch fired
              </p>
              <p className="text-red-200/90 text-xs leading-relaxed">
                Equity drew down <span className="font-mono font-bold">{killSwitch.drawdownPct?.toFixed(2)}%</span> from peak (threshold {killSwitch.thresholdPct}%). The strategy was auto-paused and{' '}
                {killSwitch.stoppedBots?.length || 0} controlled bot{killSwitch.stoppedBots?.length === 1 ? '' : 's'} stopped at{' '}
                {killSwitch.firedAt ? new Date(killSwitch.firedAt).toLocaleString() : ''}. Click <em>Activate</em> to resume with a fresh peak equity reference.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3 risk panel. Only meaningful when capitalBudget is set (every
          new strategy will have it, but older strategies may not). Shows budget,
          live equity, drawdown — the three numbers a user needs to trust the
          kill-switch will actually fire when it should. */}
      {capitalBudget > 0 && (
        <div className="rounded-xl border border-dark-700/40 bg-dark-700/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Risk
          </p>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <Stat label="Capital budget" value={`$${capitalBudget.toFixed(2)}`} />
            <Stat label="Peak equity" value={`$${peakEquity.toFixed(2)}`} />
            <Stat label="Current equity" value={`$${lastEquity.toFixed(2)}`} />
            <Stat
              label="Drawdown"
              value={maxDD ? `${drawdownPct.toFixed(2)}% / ${maxDD}%` : `${drawdownPct.toFixed(2)}%`}
            />
          </div>
          {maxDD && !killSwitch && drawdownPct > maxDD * 0.7 && (
            <p className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Approaching kill-switch threshold ({drawdownPct.toFixed(2)}% of {maxDD}%).
            </p>
          )}
        </div>
      )}

      {/* Currently-controlled inner bot — only meaningful when auto-execute
          has actually deployed something. */}
      {autoExecute && controlledBots.length > 0 && (
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3">
          <p className="text-[10px] uppercase tracking-wider text-violet-300 font-bold mb-1.5 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Currently running ({controlledBots.length})
          </p>
          <div className="space-y-1.5">
            {controlledBots.map((c) => (
              <div key={c.botId} className="flex items-center justify-between text-xs">
                <div>
                  <span className="text-white font-semibold">
                    {c.botType === 'grid' ? 'Grid Bot' : c.botType === 'dca' ? 'DCA Safety' : c.botType}
                  </span>
                  <span className="text-gray-500 font-mono ml-2">{c.botId.slice(0, 8)}…</span>
                </div>
                <span className="text-gray-500 text-[10px]">
                  Deployed {new Date(c.deployedAt).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active state */}
      {state ? (
        <div className={`p-4 rounded-xl border ${COLOR_CLASSES[regimeM?.color] || COLOR_CLASSES.amber}`}>
          <div className="flex items-start gap-3">
            <RegimeIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider font-bold">Currently: {regimeM?.label || regime?.regime}</p>
              <p className="text-sm font-bold text-white mt-1">→ {state.activeRuleName}</p>
              {state.activeStrategyId && <p className="text-xs text-gray-400 font-mono mt-0.5">Active strategy: {state.activeStrategyId}</p>}
              {state.activeAction === 'pause' && <p className="text-xs text-amber-400 mt-0.5">⏸ Action: pause all bots</p>}
              {state.isFallback && <p className="text-[10px] text-gray-500 mt-1">(no rule matched — fallback)</p>}
            </div>
          </div>
          {regime && (
            <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
              <Stat label="Trend strength" value={regime.trendStrength?.toFixed(2)} />
              <Stat label="Volatility (BB %)" value={`${regime.volatilityPct?.toFixed(2)}%`} />
              <Stat label="RSI" value={regime.rsi?.toFixed(1)} />
            </div>
          )}
          <p className="text-[9px] text-gray-500 mt-2">Last evaluated {new Date(state.lastEvaluatedAt).toLocaleTimeString()} · auto-evaluates every 5min</p>
        </div>
      ) : (
        <div className="bg-dark-700/30 rounded-xl p-4 text-center">
          <p className="text-xs text-gray-500">{config.status === 'active' ? 'Awaiting first evaluation…' : 'Activate to start evaluating'}</p>
        </div>
      )}

      {/* Rules list */}
      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Rules ({config.rules.length})</p>
        <div className="space-y-2">
          {config.rules.map((r, i) => {
            const isActive = state?.activeRuleId === r.id;
            return (
              <div key={r.id} className={`p-3 rounded-lg border ${isActive ? 'bg-violet-500/10 border-violet-400/40' : 'bg-dark-700/30 border-dark-600/30'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white flex items-center gap-2">
                    {isActive && <CheckCircle className="w-3.5 h-3.5 text-violet-400" />}
                    {r.name}
                  </span>
                  <span className="text-[10px] text-gray-500">#{i + 1}</span>
                </div>
                <p className="text-[10px] text-gray-400 font-mono">{describeWhen(r.when)}</p>
                <p className="text-[10px] text-gray-300 mt-0.5">→ {r.thenAction === 'pause' ? '⏸ Pause' : r.thenStrategyId || '—'}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function describeWhen(when) {
  if (!when) return 'always';
  const parts = [];
  if (when.regime) parts.push(`regime ∈ [${Array.isArray(when.regime) ? when.regime.join(', ') : when.regime}]`);
  if (when.minTrend !== undefined) parts.push(`trend ≥ ${when.minTrend}`);
  if (when.maxTrend !== undefined) parts.push(`trend ≤ ${when.maxTrend}`);
  if (when.maxVolatilityPct !== undefined) parts.push(`vol% ≤ ${when.maxVolatilityPct}`);
  if (when.minVolatilityPct !== undefined) parts.push(`vol% ≥ ${when.minVolatilityPct}`);
  if (when.momentum) parts.push(`momentum: ${when.momentum}`);
  if (when.rsiBelow !== undefined) parts.push(`RSI < ${when.rsiBelow}`);
  if (when.rsiAbove !== undefined) parts.push(`RSI > ${when.rsiAbove}`);
  return parts.join(' AND ') || 'always';
}

function Stat({ label, value }) {
  return (
    <div className="bg-dark-800/50 rounded p-2">
      <p className="text-gray-500">{label}</p>
      <p className="text-white font-mono text-xs">{value ?? '—'}</p>
    </div>
  );
}

// ─── Create Panel ────────────────────────────────────────────────────────────

function CreatePanel({ onCreated }) {
  const [name, setName] = useState('My regime-switcher');
  const [description, setDescription] = useState('');
  const [referenceSymbol, setReferenceSymbol] = useState('BTC/USDT');
  const [referenceExchange, setReferenceExchange] = useState('');
  const [timeframe, setTimeframe] = useState('1h');

  // Connected exchanges — the user can only pick from exchanges they've
  // connected, since fetching per-exchange markets requires their API keys.
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getExchangeConnections();
        const list = (res?.exchanges || res?.data?.exchanges || []).filter(e => e.connected);
        setConnectedExchanges(list);
        if (list.length && !referenceExchange) setReferenceExchange(list[0].exchangeId);
      } catch (err) { console.error(err); }
      finally { setLoadingExchanges(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dynamic pair list per selected exchange (same pattern as other bot pages).
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  useEffect(() => {
    if (!referenceExchange) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(referenceExchange)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(referenceSymbol)) setReferenceSymbol(sorted[0]);
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceExchange]);

  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  // Aggregate user's actual deployed bots + saved strategy templates so the
  // rule's "Then run strategy" picker reflects REAL things the user can run.
  // Previously the dropdown was a hardcoded list of placeholder labels — users
  // couldn't tell what was real vs decorative. This is the v1.1 step toward
  // full strategy orchestration: pick a real bot you've deployed, and the
  // active rule shows exactly which one to manually activate (auto-activation
  // is on the post-launch roadmap).
  const [strategyCatalog, setStrategyCatalog] = useState({ loading: true, grid: [], dca: [], smart: [], saved: [], templates: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [grid, smart, dca, saved, templates] = await Promise.all([
        listGridBots().then(r => r?.data || []).catch(() => []),
        listSmartTrades().then(r => r?.data || []).catch(() => []),
        listDcaSafetyBots().then(r => r?.data || []).catch(() => []),
        listStrategies().then(r => r?.strategies || r?.data?.strategies || []).catch(() => []),
        getStrategyTemplates().then(r => Array.isArray(r) ? r : (r?.templates || r?.data || [])).catch(() => []),
      ]);
      if (!cancelled) setStrategyCatalog({ loading: false, grid, smart, dca, saved, templates });
    })();
    return () => { cancelled = true; };
  }, []);
  // Phase 1.1 (2026-06-08): rules now carry a thenAction enum:
  //   'deploy'           — auto-deploy thenBotConfig (Grid in Phase 1)
  //   'pause'            — stop all controlled bots
  //   'continue_current' — keep whatever bot is already deployed
  //   null + thenStrategyId — legacy "signal-only" pointer to a saved strategy
  // The Range → Grid default uses the new schema to demonstrate auto-execute.
  const [rules, setRules] = useState([
    { id: 'r1', name: 'Strong uptrend → momentum',  when: { regime: ['strong_uptrend', 'uptrend'] }, thenStrategyId: 'mkt-001', thenAction: null, thenBotType: null, thenBotConfig: null },
    {
      id: 'r2',
      name: 'Range → grid',
      when: { regime: ['range'], maxVolatilityPct: 6 },
      thenStrategyId: null,
      thenAction: 'deploy',
      thenBotType: 'grid',
      thenBotConfig: {
        exchangeId: '',
        symbol: 'BTC/USDT',
        lowPrice: 0,
        highPrice: 0,
        gridCount: 10,
        totalAmount: 100,
        mode: 'arithmetic',
      },
    },
    { id: 'r3', name: 'Volatile / downtrend → pause', when: { regime: ['volatile', 'downtrend', 'strong_downtrend'] }, thenStrategyId: null, thenAction: 'pause', thenBotType: null, thenBotConfig: null },
  ]);
  const [fallbackStrategyId, setFallbackStrategyId] = useState('mkt-002');
  const [autoExecute, setAutoExecute] = useState(false);
  // Phase 3 risk knobs (2026-06-09). capitalBudget is required at create-time
  // — it sets the hard ceiling for any rule's deploy AND the equity denominator
  // for drawdown tracking. maxDrawdownPct is optional ('' = disabled).
  const [capitalBudget, setCapitalBudget] = useState(100);
  const [maxDrawdownPct, setMaxDrawdownPct] = useState(20);
  const [simulating, setSimulating] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  function updateRule(idx, field, value) {
    setRules(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }
  function updateRuleWhen(idx, field, value) {
    setRules(rs => rs.map((r, i) => i === idx ? { ...r, when: { ...r.when, [field]: value } } : r));
  }
  function updateRuleBotConfig(idx, field, value) {
    setRules(rs => rs.map((r, i) => i === idx
      ? { ...r, thenBotConfig: { ...(r.thenBotConfig || {}), [field]: value } }
      : r));
  }
  // Default deploy-config templates per inner bot type. Centralized so
  // setRuleAction and setRuleBotType produce the same initial shape, and so
  // adding a new inner bot type (Smart Trade, Stop Order) later is a one-line
  // change here instead of hunting through both helpers.
  function defaultBotConfig(botType) {
    if (botType === 'dca') {
      return {
        exchangeId: referenceExchange || '',
        symbol: referenceSymbol || 'BTC/USDT',
        baseAmount: 50,
        soCount: 5,
        soStepPct: 1.5,
        soStepScale: 1.5,
        soVolume: 50,
        soVolumeScale: 1.5,
        tpPct: 1.5,
      };
    }
    // grid (default)
    return {
      exchangeId: referenceExchange || '',
      symbol: referenceSymbol || 'BTC/USDT',
      lowPrice: 0,
      highPrice: 0,
      gridCount: 10,
      totalAmount: 100,
      mode: 'arithmetic',
    };
  }

  // Switching the rule's action wipes incompatible peer fields so we never
  // send a half-populated mixed-mode rule to the backend validator.
  function setRuleAction(idx, action) {
    setRules(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      if (action === 'deploy') {
        const botType = r.thenBotType || 'grid';
        return {
          ...r,
          thenAction: 'deploy',
          thenStrategyId: null,
          thenBotType: botType,
          thenBotConfig: r.thenBotConfig || defaultBotConfig(botType),
        };
      }
      if (action === 'pause') {
        return { ...r, thenAction: 'pause', thenStrategyId: null, thenBotType: null, thenBotConfig: null };
      }
      if (action === 'continue_current') {
        return { ...r, thenAction: 'continue_current', thenStrategyId: null, thenBotType: null, thenBotConfig: null };
      }
      // signal-only — keep thenStrategyId, clear everything else
      return { ...r, thenAction: null, thenBotType: null, thenBotConfig: null };
    }));
  }

  // Swap the inner bot type (Grid ↔ DCA) on a rule already in Deploy mode.
  // Always resets thenBotConfig — Grid and DCA configs share no fields, so
  // preserving the old one would just produce immediate validator errors.
  function setRuleBotType(idx, botType) {
    setRules(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      return { ...r, thenBotType: botType, thenBotConfig: defaultBotConfig(botType) };
    }));
  }
  function addRule() {
    setRules(rs => [...rs, { id: `r${rs.length + 1}`, name: 'New rule', when: { regime: ['range'] }, thenStrategyId: 'mkt-002', thenAction: null, thenBotType: null, thenBotConfig: null }]);
  }
  function removeRule(idx) { setRules(rs => rs.filter((_, i) => i !== idx)); }

  // Action a rule is currently in. Maps the legacy {thenStrategyId,thenAction}
  // pair onto the new four-way enum the UI shows.
  function ruleActionOf(r) {
    if (r.thenAction === 'deploy') return 'deploy';
    if (r.thenAction === 'pause') return 'pause';
    if (r.thenAction === 'continue_current') return 'continue_current';
    return 'signal';
  }

  function buildConfig() {
    return {
      name, description, referenceSymbol, referenceExchange, timeframe,
      rules, fallbackStrategyId,
      autoExecute,
      capitalBudget: Number(capitalBudget) || 0,
      // Empty string from the input → null, which disables the kill-switch
      // server-side. Any positive value enables it.
      maxDrawdownPct: maxDrawdownPct === '' || maxDrawdownPct == null
        ? null
        : Number(maxDrawdownPct),
    };
  }

  async function handleSimulate() {
    setSimulating(true);
    setError(null);
    try {
      const res = await simulateConditionalStrategy(buildConfig());
      setSimulation(res?.data);
    } catch (err) { setError(err.message); }
    finally { setSimulating(false); }
  }

  async function handleCreate() {
    setCreating(true);
    setError(null);
    try {
      await createConditionalStrategy(buildConfig());
      onCreated();
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Strategy name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Description (optional)</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Exchange (source of OHLCV)</label>
            {loadingExchanges ? (
              <div className="bg-dark-700/50 rounded-lg p-2.5 text-gray-500 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : connectedExchanges.length === 0 ? (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 text-amber-400 text-xs">
                No exchange connected. Add one in <a href="/settings?tab=connections" className="underline">Settings</a>.
              </div>
            ) : (
              <select value={referenceExchange} onChange={e => setReferenceExchange(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm">
                {connectedExchanges.map(c => <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Timeframe
              <InfoTip side="top">The candle interval the regime classifier uses. 1h is the standard — balances responsiveness and noise. 15m = reacts faster but more false signals. 4h-1d = smoother but slower to catch regime shifts.</InfoTip>
            </label>
            <div className="flex gap-1">
              {TIMEFRAMES.map(t => (
                <button key={t} type="button"
                  onClick={() => setTimeframe(t)}
                  className={`flex-1 px-2 py-2 text-xs font-mono rounded-lg border transition-colors ${
                    timeframe === t
                      ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                      : 'bg-dark-700 border-dark-600 text-gray-400 hover:border-dark-500'
                  }`}>{t}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Searchable per-exchange pair list — same pattern as Algo Orders /
            Grid Bot / DCA Safety. Replaces the old hardcoded 4-pair dropdown. */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-gray-400 flex items-center gap-1.5">
              Reference symbol (what's regime is classified from)
              <InfoTip side="right">The ONE symbol used for regime detection. BTC/USDT is standard — BTC drives the broader crypto market. Even if your inner bots trade SOL/ETH, regime decisions made from BTC are typically more reliable.</InfoTip>
            </label>
            <span className="text-[10px] text-gray-500">
              {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
            </span>
          </div>
          <input
            type="text"
            value={pairSearch}
            onChange={(e) => setPairSearch(e.target.value)}
            placeholder="Search pair (e.g. BTC, ETH/USDC)"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono placeholder-gray-600 mb-2"
          />
          <div className="max-h-40 overflow-y-auto bg-dark-700/50 border border-dark-600 rounded-lg">
            {filteredPairs.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">No pairs match "{pairSearch}"</div>
            ) : (
              filteredPairs.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReferenceSymbol(p)}
                  className={`block w-full text-left px-3 py-1.5 text-sm font-mono transition-colors ${
                    referenceSymbol === p
                      ? 'bg-violet-500/15 text-violet-300'
                      : 'text-gray-300 hover:bg-dark-600'
                  }`}
                >
                  {p}
                </button>
              ))
            )}
          </div>
          {pairs.length > 100 && !pairSearch && (
            <p className="text-[10px] text-gray-500 mt-1">Showing first 100 — refine search to find more</p>
          )}
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            Selected: <span className="font-mono text-white">{referenceSymbol || '(none)'}</span>
            <RefSymbolPriceTag exchangeId={referenceExchange} symbol={referenceSymbol} />
          </div>
        </div>
      </div>

      {/* Phase 3 risk knobs (2026-06-09). Required capital budget + optional
          drawdown kill-switch. Sits above the rules so users decide their max
          exposure FIRST and the rule editor warns if a rule's deploy exceeds it. */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Risk limits
          <InfoTip side="right">
            Capital budget is the hard ceiling — no single rule's deploy can request more capital than this. Drawdown kill-switch (optional) auto-pauses the strategy and stops all controlled bots when peak-to-trough equity drops by this percent. Defaults are conservative; set higher only after you've tested with smaller amounts.
          </InfoTip>
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Capital budget (quote)</label>
            <input
              type="number"
              step="any"
              value={capitalBudget}
              onChange={(e) => setCapitalBudget(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono"
              placeholder="100"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Hard ceiling per rule deploy. Today: <span className="font-mono text-violet-300">${Number(capitalBudget || 0).toFixed(2)}</span>
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Drawdown kill-switch %</label>
            <input
              type="number"
              step="0.1"
              value={maxDrawdownPct}
              onChange={(e) => setMaxDrawdownPct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono"
              placeholder="20"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              {maxDrawdownPct === '' || maxDrawdownPct == null
                ? 'Disabled — strategy runs without a hard drawdown stop.'
                : `Auto-pause at ${Number(maxDrawdownPct).toFixed(1)}% drawdown from peak equity.`}
            </p>
          </div>
        </div>
        {/* Sum-of-deploys sanity check — Conditional only runs ONE bot at a
            time, so we don't add rule capitals, but we DO warn if any single
            rule exceeds the budget (matches the server-side validator). */}
        {(() => {
          const overRules = rules
            .filter(r => r.thenAction === 'deploy')
            .map(r => ({
              name: r.name,
              need: r.thenBotType === 'grid'
                ? Number(r.thenBotConfig?.totalAmount) || 0
                : r.thenBotType === 'dca'
                  ? ((Number(r.thenBotConfig?.baseAmount) || 0) +
                     (() => {
                       let t = 0;
                       let v = Number(r.thenBotConfig?.soVolume) || 0;
                       const s = Number(r.thenBotConfig?.soVolumeScale) || 1;
                       for (let i = 0; i < (Number(r.thenBotConfig?.soCount) || 0); i++) { t += v; v *= s; }
                       return t;
                     })())
                  : 0,
            }))
            .filter(r => r.need > (Number(capitalBudget) || 0));
          if (!overRules.length) return null;
          return (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 text-amber-300 text-xs space-y-1">
              <p className="font-semibold">{overRules.length} rule{overRules.length === 1 ? '' : 's'} exceed the budget:</p>
              {overRules.map((r, i) => (
                <p key={i} className="font-mono text-[11px]">
                  "{r.name}" needs ${r.need.toFixed(2)}
                </p>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Auto-execute master switch — Phase 1.1 (2026-06-08). Defaulted OFF so
          the first save is signal-only; user opts in to bot orchestration. */}
      <div className={`rounded-2xl border p-4 ${autoExecute ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-dark-800/50 border-dark-700/50'}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={autoExecute}
            onChange={(e) => setAutoExecute(e.target.checked)}
            className="mt-0.5 accent-emerald-500"
          />
          <div className="flex-1">
            <p className="text-white text-sm font-semibold flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              Auto-execute on regime switch
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${autoExecute ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-gray-500/15 text-gray-400 border border-gray-500/30'}`}>
                {autoExecute ? 'ON' : 'OFF'}
              </span>
            </p>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">
              {autoExecute
                ? "When the regime changes, the engine stops the previous rule's inner bot and deploys the new rule's bot — Grid Bot or DCA Safety, set-and-forget. Rules with action 'Deploy bot' must have a valid config filled in below. Rate-limited to one switch per 30 min."
                : "Signal-only — the engine evaluates regimes and tells you which rule is active, but does not start or stop any bots. Toggle on to make this strategy act autonomously."}
            </p>
          </div>
        </label>
      </div>

      {/* Rules */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-violet-400" /> Rules (evaluated top-down — first match wins)
            <InfoTip side="right">Rules fire top-to-bottom; the FIRST match wins. Order from most-restrictive (pause conditions, extreme regimes) to least-restrictive (fallback always-active). Max 10 rules per strategy.</InfoTip>
          </h3>
          <button onClick={addRule} disabled={rules.length >= 10}
            className="text-xs flex items-center gap-1 text-violet-400 hover:text-violet-300 disabled:opacity-30">
            <Plus className="w-3 h-3" /> Add Rule
          </button>
        </div>

        {rules.map((r, i) => {
          const action = ruleActionOf(r);
          return (
          <div key={r.id} className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30 space-y-2">
            <div className="flex items-center justify-between">
              <input value={r.name} onChange={e => updateRule(i, 'name', e.target.value)}
                className="flex-1 bg-transparent text-sm font-bold text-white border-none outline-none" />
              <button onClick={() => removeRule(i)} className="text-gray-500 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-gray-500 flex items-center gap-1.5">
                  When regime is
                  <InfoTip side="top">Hold Ctrl/Cmd to select multiple regimes. Common patterns: [Strong Downtrend, High Volatility] → Pause. [Range/Sideways] → Grid Bot. [Uptrend, Strong Uptrend] → Smart Trade momentum.</InfoTip>
                </label>
                <select multiple value={r.when?.regime || []}
                  onChange={e => updateRuleWhen(i, 'regime', Array.from(e.target.selectedOptions).map(o => o.value))}
                  className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs h-24">
                  {REGIMES.map(rg => <option key={rg.id} value={rg.id}>{rg.label}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-1 mt-2">
                  <div>
                    <label className="text-[9px] text-gray-500 flex items-center gap-1">
                      Max volatility %
                      <InfoTip side="top">Optional. Only fires this rule when Bollinger Band width is ≤ this % of price. Use ~6 on a Range rule so very-high-volatility periods don't qualify as "range". Leave blank to skip.</InfoTip>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="(optional)"
                      value={r.when?.maxVolatilityPct ?? ''}
                      onChange={e => updateRuleWhen(i, 'maxVolatilityPct', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                      className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-gray-500 flex items-center gap-1">
                      RSI below
                      <InfoTip side="top">Optional. Only fires when RSI is BELOW this value. Useful on Downtrend rules — e.g. RSI &lt; 35 means "only deploy DCA when truly oversold". Leave blank to skip.</InfoTip>
                    </label>
                    <input
                      type="number"
                      placeholder="(optional)"
                      value={r.when?.rsiBelow ?? ''}
                      onChange={e => updateRuleWhen(i, 'rsiBelow', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                      className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] text-gray-500 flex items-center gap-1.5">
                    Action
                    <InfoTip side="top">
                      Deploy = start a fresh bot with the config below. Pause = stop any bot this strategy started. Continue = leave whatever bot is already running. Signal-only = just record the active rule without touching bots.
                    </InfoTip>
                  </label>
                  <select
                    value={action}
                    onChange={(e) => setRuleAction(i, e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs"
                  >
                    <option value="deploy">🚀 Deploy bot (auto-execute)</option>
                    <option value="pause">⏸ Pause all controlled bots</option>
                    <option value="continue_current">↻ Continue current bot</option>
                    <option value="signal">📝 Signal-only (point to a saved strategy)</option>
                  </select>
                  {!autoExecute && action === 'deploy' && (
                    <p className="text-[10px] text-amber-400 mt-1 leading-tight">
                      Auto-execute is OFF — this rule will still be recorded as active but no bot will deploy until you toggle it on.
                    </p>
                  )}
                </div>

                {action === 'signal' && (
                  <div>
                    <label className="text-[10px] text-gray-500">Then run strategy (signal-only)</label>
                    <StrategyPicker
                      value={r.thenStrategyId || ''}
                      onChange={(v) => updateRule(i, 'thenStrategyId', v || null)}
                      catalog={strategyCatalog}
                      small
                    />
                  </div>
                )}

                {action === 'deploy' && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] text-gray-500 flex items-center gap-1.5">
                        Bot type to deploy
                        <InfoTip side="top">
                          Grid = profits from oscillation inside a range. DCA Safety = averages down through a dip with safety orders + take-profit. Pair them: Grid for range regimes, DCA for downtrends.
                        </InfoTip>
                      </label>
                      <select
                        value={r.thenBotType || 'grid'}
                        onChange={(e) => setRuleBotType(i, e.target.value)}
                        className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs"
                      >
                        <option value="grid">🟢 Grid Bot</option>
                        <option value="dca">🟣 DCA Safety</option>
                      </select>
                    </div>

                    {r.thenBotType === 'dca' ? (
                      <DcaSafetyConfigSubForm
                        cfg={r.thenBotConfig || {}}
                        onChange={(field, value) => updateRuleBotConfig(i, field, value)}
                        connectedExchanges={connectedExchanges}
                      />
                    ) : (
                      <GridBotConfigSubForm
                        cfg={r.thenBotConfig || {}}
                        onChange={(field, value) => updateRuleBotConfig(i, field, value)}
                        connectedExchanges={connectedExchanges}
                      />
                    )}
                  </div>
                )}

                {action === 'pause' && (
                  <p className="text-[10px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded p-2 leading-relaxed">
                    All bots this strategy previously deployed will be stopped when this rule activates.
                  </p>
                )}

                {action === 'continue_current' && (
                  <p className="text-[10px] text-violet-300/80 bg-violet-500/5 border border-violet-500/20 rounded p-2 leading-relaxed">
                    Whatever bot is currently running stays running. Useful for transitional regimes you don't want to react to.
                  </p>
                )}
              </div>
            </div>
          </div>
          );
        })}

        <div className="border-t border-dark-700/50 pt-3">
          <label className="text-xs text-gray-400 mb-1 block">Fallback (if no rule matches)</label>
          <StrategyPicker
            value={fallbackStrategyId || ''}
            onChange={(v) => setFallbackStrategyId(v || null)}
            catalog={strategyCatalog}
            placeholder="— pause all bots —"
          />
        </div>
      </div>

      {error && <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs">{error}</div>}

      {/* Simulation preview */}
      {simulation && (
        <div className="bg-dark-800/50 rounded-2xl border border-violet-500/30 p-5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-violet-400" /> Simulation preview (current real-market conditions)
          </h3>
          <div className="grid grid-cols-3 gap-2 text-xs mb-3">
            <Stat label="Detected regime" value={regimeMeta(simulation.regime?.regime).label} />
            <Stat label="Trend strength" value={simulation.regime?.trendStrength?.toFixed(2)} />
            <Stat label="Volatility (BB %)" value={`${simulation.regime?.volatilityPct?.toFixed(2)}%`} />
          </div>
          <p className="text-sm text-white">→ <span className="font-bold">{simulation.activeRule?.name}</span> {simulation.isFallback && <span className="text-[10px] text-gray-500">(fallback)</span>}</p>
          {simulation.activeRule?.thenStrategyId && <p className="text-xs text-gray-400 font-mono mt-0.5">Strategy: {simulation.activeRule.thenStrategyId}</p>}
          {simulation.activeRule?.thenAction === 'pause' && <p className="text-xs text-amber-400 mt-0.5">Action: pause all bots</p>}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={handleSimulate} disabled={simulating}
          className="flex-1 px-4 py-3 rounded-xl border border-violet-500/30 bg-violet-500/10 text-violet-400 text-sm font-semibold hover:bg-violet-500/20 disabled:opacity-50 flex items-center justify-center gap-2">
          {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          {simulating ? 'Simulating…' : 'Preview against current market'}
        </button>
        <button onClick={handleCreate} disabled={creating}
          className="flex-1 bg-violet-500 hover:bg-violet-400 disabled:bg-dark-600 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {creating ? 'Creating…' : 'Create strategy (paused)'}
        </button>
      </div>

      <p className="text-[10px] text-gray-500 text-center">
        Strategies are created paused. Activate from the My Strategies tab to start evaluating every 5 minutes against real OHLCV.
      </p>
    </div>
  );
}

// ─── RefSymbolPriceTag ───────────────────────────────────────────────────────
//
// Small badge that shows the current live price of the chosen reference symbol
// right next to "Selected: BTC/USDT". Saves the user from opening another tab
// to look up "where is BTC right now" while building the strategy.

function RefSymbolPriceTag({ exchangeId, symbol }) {
  const { price, stale } = useLivePrice(exchangeId, symbol);
  if (!exchangeId || !symbol) return null;
  if (price == null) {
    return (
      <span className="font-mono text-[10px] text-gray-600">
        {stale ? 'price unavailable' : 'loading…'}
      </span>
    );
  }
  return (
    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20">
      live: ${price.toLocaleString(undefined, { maximumFractionDigits: price < 1 ? 6 : 2 })}
    </span>
  );
}

// ─── StrategyPicker ──────────────────────────────────────────────────────────
//
// Unified picker that pulls from the user's actually deployed bots (Grid,
// DCA Safety, Smart Trade) plus saved strategy templates. Replaces the old
// hardcoded list of placeholder labels. Each option encodes its source as a
// prefix (grid:/dca:/smart:/template:/legacy:) so the evaluator can later
// route execution to the right system.
//
// Why prefix instead of raw IDs: bot IDs across different sources COULD
// collide (rare with UUIDs but possible), and the prefix makes the rule
// human-readable when displayed elsewhere ("→ run grid:abc123" tells you
// it's a grid bot at a glance).

function StrategyPicker({ value, onChange, catalog, placeholder = '— select —', small = false }) {
  const cls = small
    ? 'w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-xs'
    : 'w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm';

  // Build the option groups from the catalog. Each entry's id has source prefix.
  function fmt(label, status) {
    const s = status ? ` · ${status}` : '';
    return `${label}${s}`;
  }

  const gridOpts  = (catalog.grid || []).map(b => ({ id: `grid:${b.botId}`,   label: fmt(`${b.name || b.botId} (${b.exchangeId} ${b.symbol})`, b.status) }));
  const dcaOpts   = (catalog.dca  || []).map(b => ({ id: `dca:${b.botId}`,    label: fmt(`${b.name || b.botId} (${b.exchangeId} ${b.symbol})`, b.status) }));
  const smartOpts = (catalog.smart || []).map(t => ({ id: `smart:${t.tradeId}`, label: fmt(`${t.name || t.tradeId} (${t.exchangeId || ''} ${t.symbol || ''})`, t.status) }));
  const savedOpts = (catalog.saved || []).map(s => ({ id: `strategy:${s.strategyId}`, label: `${s.name}${s.category ? ` (${s.category})` : ''}` }));
  const tplOpts   = (catalog.templates || []).map(t => ({ id: `template:${t.id || t.templateId}`, label: t.name || t.label || t.id || t.templateId }));

  // Legacy hardcoded options that prior rules might still reference. We keep
  // them so existing strategies don't break, but the values are now prefixed
  // with `legacy:` for clarity.
  const LEGACY = [
    { id: 'legacy:mkt-001',     label: 'Alpha Momentum Pro (trend) — generic label' },
    { id: 'legacy:mkt-002',     label: 'DCA Guardian (mean reversion) — generic label' },
    { id: 'legacy:mkt-003',     label: 'Breakout Hunter (high vol) — generic label' },
    { id: 'legacy:mkt-004',     label: 'Bollinger Squeeze Pro (range) — generic label' },
    { id: 'legacy:grid-tight',  label: 'Grid Bot (tight range) — generic label' },
    { id: 'legacy:grid-medium', label: 'Grid Bot (medium range) — generic label' },
    { id: 'legacy:grid-wide',   label: 'Grid Bot (wide range) — generic label' },
  ];

  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={cls}>
      <option value="">{catalog.loading ? 'Loading your bots…' : placeholder}</option>

      {gridOpts.length > 0 && (
        <optgroup label="🟢 Your Grid Bots">
          {gridOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </optgroup>
      )}

      {dcaOpts.length > 0 && (
        <optgroup label="🟣 Your DCA Safety Bots">
          {dcaOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </optgroup>
      )}

      {smartOpts.length > 0 && (
        <optgroup label="🟡 Your Smart Trades">
          {smartOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </optgroup>
      )}

      {savedOpts.length > 0 && (
        <optgroup label="💾 Your Saved Strategies (from Strategy Builder)">
          {savedOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </optgroup>
      )}

      {tplOpts.length > 0 && (
        <optgroup label="📋 Starter Templates">
          {tplOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </optgroup>
      )}

      <optgroup label="📝 Generic strategy labels (advisory)">
        {LEGACY.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </optgroup>
    </select>
  );
}

// ─── GridBotConfigSubForm ────────────────────────────────────────────────────
//
// Inline editor for the Grid Bot params auto-deployed when this rule activates
// and the strategy's master `autoExecute` switch is ON. Matches the shape
// gridBotEngine.validateConfig() expects: exchangeId, symbol, lowPrice,
// highPrice, gridCount, totalAmount, mode. We deliberately keep it compact —
// power-users wanting fancy grid features (geometric mode, futures leverage)
// should build the Grid Bot directly and use Signal-only here.

function GridBotConfigSubForm({ cfg, onChange, connectedExchanges }) {
  const num = (v) => (v === '' ? '' : Number(v));
  const { price, stale } = useLivePrice(cfg.exchangeId, cfg.symbol);

  // Quick-fill helper — sets low + high to ±pct from the live price so the
  // user doesn't have to multiply in their head. Rounded to 4 sig figs for
  // readability ($2,500.12 not $2500.1234567).
  function fillRange(pct) {
    if (!price) return;
    const round = (n) => Number(n.toFixed(n < 1 ? 6 : 2));
    onChange('lowPrice', round(price * (1 - pct / 100)));
    onChange('highPrice', round(price * (1 + pct / 100)));
  }

  // Sanity: is current price inside the chosen range? Engine rejects the
  // deploy if not, so we warn here instead of letting it fail at deploy time.
  const inRange = price && Number(cfg.lowPrice) > 0 && Number(cfg.highPrice) > 0
    ? price >= Number(cfg.lowPrice) && price <= Number(cfg.highPrice)
    : null;

  return (
    <div className="bg-dark-800/60 border border-violet-500/20 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-violet-300 font-bold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Grid Bot to auto-deploy
        </p>
        {cfg.exchangeId && cfg.symbol && (
          <span className="font-mono text-[10px]">
            {price != null ? (
              <span className="text-violet-300 px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-500/20">
                live: ${price.toLocaleString(undefined, { maximumFractionDigits: price < 1 ? 6 : 2 })}
              </span>
            ) : (
              <span className="text-gray-600">{stale ? 'price unavailable' : 'loading…'}</span>
            )}
          </span>
        )}
      </div>

      {/* Quick-fill buttons — set low/high to ±pct from live price.
          Hidden until both exchange + symbol are picked. */}
      {price != null && (
        <div className="flex items-center gap-1 text-[10px]">
          <span className="text-gray-500">Fill range:</span>
          {[1, 2, 5, 10].map(pct => (
            <button
              key={pct}
              type="button"
              onClick={() => fillRange(pct)}
              className="px-1.5 py-0.5 rounded bg-dark-700 border border-dark-600 text-gray-300 hover:bg-violet-500/10 hover:border-violet-500/30 hover:text-violet-300 font-mono"
            >
              ±{pct}%
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">Exchange</label>
          <select
            value={cfg.exchangeId || ''}
            onChange={(e) => onChange('exchangeId', e.target.value)}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px]"
          >
            <option value="">— pick —</option>
            {connectedExchanges.map(c => (
              <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Symbol</label>
          <input
            type="text"
            value={cfg.symbol || ''}
            onChange={(e) => onChange('symbol', e.target.value.toUpperCase())}
            placeholder="BTC/USDT"
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">Low price</label>
          <input
            type="number"
            step="any"
            value={cfg.lowPrice ?? ''}
            onChange={(e) => onChange('lowPrice', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">High price</label>
          <input
            type="number"
            step="any"
            value={cfg.highPrice ?? ''}
            onChange={(e) => onChange('highPrice', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">Grids</label>
          <input
            type="number"
            min="2"
            max="100"
            value={cfg.gridCount ?? ''}
            onChange={(e) => onChange('gridCount', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Capital (quote)</label>
          <input
            type="number"
            step="any"
            value={cfg.totalAmount ?? ''}
            onChange={(e) => onChange('totalAmount', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Mode</label>
          <select
            value={cfg.mode || 'arithmetic'}
            onChange={(e) => onChange('mode', e.target.value)}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px]"
          >
            <option value="arithmetic">Arithmetic</option>
            <option value="geometric">Geometric</option>
          </select>
        </div>
      </div>

      {inRange === false && (
        <p className="text-[10px] text-amber-300 bg-amber-500/5 border border-amber-500/20 rounded p-1.5 leading-relaxed">
          Live price <span className="font-mono">${price?.toFixed(2)}</span> is outside <span className="font-mono">[${Number(cfg.lowPrice).toFixed(2)}, ${Number(cfg.highPrice).toFixed(2)}]</span>. Deploy will fail unless price re-enters by the time the rule fires.
        </p>
      )}

      <p className="text-[9px] text-gray-500 leading-relaxed">
        At deploy time the engine validates current price is inside [low, high]. If you set the range too tight, the deploy will fail and the strategy will record the error — no partial state.
      </p>
    </div>
  );
}

// ─── DcaSafetyConfigSubForm ──────────────────────────────────────────────────
//
// Phase 2 (2026-06-09). Inline editor for the DCA Safety params auto-deployed
// when this rule activates. Matches dcaSafetyEngine.validateConfig: exchangeId,
// symbol, baseAmount (quote currency), soCount, soStepPct, soStepScale,
// soVolume, soVolumeScale, tpPct. We show a live "required capital" estimate
// using the same formula the engine uses (sum of base + geometric SO ladder)
// so users see what they're committing per deploy at a glance.

function DcaSafetyConfigSubForm({ cfg, onChange, connectedExchanges }) {
  const num = (v) => (v === '' ? '' : Number(v));
  const { price, stale } = useLivePrice(cfg.exchangeId, cfg.symbol);

  // Mirror of computeRequiredCapital from dcaSafetyEngine — kept in lockstep so
  // the user-facing estimate matches what the engine actually charges.
  const requiredCapital = (() => {
    if (!cfg.baseAmount || !cfg.soCount || !cfg.soVolume) return null;
    let total = Number(cfg.baseAmount) || 0;
    let v = Number(cfg.soVolume) || 0;
    const scale = Number(cfg.soVolumeScale) || 1;
    for (let i = 0; i < cfg.soCount; i++) {
      total += v;
      v *= scale;
    }
    return total;
  })();

  // Preview where the SO ladder triggers + where TP sits, anchored to live
  // price. Uses the same step-scale formula as the engine so what the user
  // sees here is exactly what gets placed on deploy.
  const ladder = (() => {
    if (!price || !cfg.soCount || !cfg.soStepPct) return null;
    const levels = [];
    let stepPct = Number(cfg.soStepPct) || 0;
    const stepScale = Number(cfg.soStepScale) || 1;
    let cumPct = 0;
    for (let i = 0; i < cfg.soCount; i++) {
      cumPct += stepPct;
      levels.push({ pct: cumPct, price: price * (1 - cumPct / 100) });
      stepPct *= stepScale;
    }
    const tp = price * (1 + (Number(cfg.tpPct) || 0) / 100);
    return { levels, tp };
  })();

  return (
    <div className="bg-dark-800/60 border border-violet-500/20 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-violet-300 font-bold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> DCA Safety bot to auto-deploy
        </p>
        {cfg.exchangeId && cfg.symbol && (
          <span className="font-mono text-[10px]">
            {price != null ? (
              <span className="text-violet-300 px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-500/20">
                live: ${price.toLocaleString(undefined, { maximumFractionDigits: price < 1 ? 6 : 2 })}
              </span>
            ) : (
              <span className="text-gray-600">{stale ? 'price unavailable' : 'loading…'}</span>
            )}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">Exchange</label>
          <select
            value={cfg.exchangeId || ''}
            onChange={(e) => onChange('exchangeId', e.target.value)}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px]"
          >
            <option value="">— pick —</option>
            {connectedExchanges.map(c => (
              <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Symbol</label>
          <input
            type="text"
            value={cfg.symbol || ''}
            onChange={(e) => onChange('symbol', e.target.value.toUpperCase())}
            placeholder="BTC/USDT"
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">Base order (quote)</label>
          <input
            type="number"
            step="any"
            value={cfg.baseAmount ?? ''}
            onChange={(e) => onChange('baseAmount', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Take-profit %</label>
          <input
            type="number"
            step="0.1"
            value={cfg.tpPct ?? ''}
            onChange={(e) => onChange('tpPct', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">SO count</label>
          <input
            type="number"
            min="1"
            max="20"
            value={cfg.soCount ?? ''}
            onChange={(e) => onChange('soCount', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">SO step %</label>
          <input
            type="number"
            step="0.1"
            value={cfg.soStepPct ?? ''}
            onChange={(e) => onChange('soStepPct', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Step scale</label>
          <input
            type="number"
            step="0.1"
            min="1"
            max="5"
            value={cfg.soStepScale ?? ''}
            onChange={(e) => onChange('soStepScale', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-gray-500">SO volume (quote)</label>
          <input
            type="number"
            step="any"
            value={cfg.soVolume ?? ''}
            onChange={(e) => onChange('soVolume', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] text-gray-500">Volume scale</label>
          <input
            type="number"
            step="0.1"
            min="1"
            max="5"
            value={cfg.soVolumeScale ?? ''}
            onChange={(e) => onChange('soVolumeScale', num(e.target.value))}
            className="w-full bg-dark-800 border border-dark-600 rounded p-1 text-white text-[11px] font-mono"
          />
        </div>
      </div>

      {requiredCapital !== null && (
        <p className="text-[10px] text-violet-300/90 bg-violet-500/5 border border-violet-500/20 rounded p-1.5">
          Capital required per deploy: <span className="font-mono font-bold">${requiredCapital.toFixed(2)}</span>{' '}
          <span className="text-gray-500">(base + {cfg.soCount || 0} SO ladder)</span>
        </p>
      )}

      {/* Live ladder preview — shows the user exactly where the SOs will sit
          and where TP will fire, anchored to the current market price. Numbers
          recompute as the user edits any of step%/scale/tp%. */}
      {ladder && (
        <div className="bg-dark-700/40 border border-dark-600/30 rounded p-1.5 text-[10px] font-mono space-y-0.5">
          <p className="text-gray-500 mb-0.5">Preview at live price <span className="text-violet-300">${price.toFixed(2)}</span>:</p>
          <p className="text-emerald-400">
            ↑ TP: <span className="text-white">${ladder.tp.toFixed(2)}</span> <span className="text-gray-500">(+{Number(cfg.tpPct).toFixed(2)}%)</span>
          </p>
          <p className="text-violet-300">
            • Base buy: <span className="text-white">${price.toFixed(2)}</span> (market)
          </p>
          {ladder.levels.map((l, i) => (
            <p key={i} className="text-red-300">
              ↓ SO{i + 1}: <span className="text-white">${l.price.toFixed(2)}</span> <span className="text-gray-500">(-{l.pct.toFixed(2)}%)</span>
            </p>
          ))}
        </div>
      )}

      <p className="text-[9px] text-gray-500 leading-relaxed">
        Step % and scale grow the SO spacing; volume scale grows each SO's size. Auto-recover is ON by default — the bot starts a fresh cycle after each TP fills until the strategy switches regimes.
      </p>
    </div>
  );
}
